import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase.config";

/**
 * Check if a user is under 17 and should be age restricted
 * @returns {Promise<boolean>} - True if user is under 17, false otherwise
 */
export const checkAgeRestriction = async (user) => {
  // If no user or user is anonymous, no age restriction
  if (!user || user.isAnonymous) {
    return false;
  }

  try {
    const userProfileRef = doc(db, "public-profiles", user.uid);
    const userProfileSnap = await getDoc(userProfileRef);

    if (userProfileSnap.exists()) {
      const userData = userProfileSnap.data();
      return userData.isUnder17 === true;
    }

    // If profile doesn't exist, no age restriction
    return false;
  } catch (error) {
    console.error("Error checking age restriction:", error);
    // In case of error, assume no age restriction to be safe
    return false;
  }
};

/**
 * Check if a user has completed onboarding
 * @param {Object} user - The current user object from Firebase Auth
 * @returns {Promise<boolean>} - True if user has completed onboarding, false otherwise
 */
export const checkOnboardingStatus = async (user) => {
  // If no user or user is anonymous, consider onboarding not required
  if (!user || user.isAnonymous) {
    return true; // Don't force anonymous users through onboarding
  }

  try {
    const userProfileRef = doc(db, "public-profiles", user.uid);
    const userProfileSnap = await getDoc(userProfileRef);

    if (userProfileSnap.exists()) {
      const userData = userProfileSnap.data();
      return userData.onboardingCompleted === true;
    }

    // If profile doesn't exist, user hasn't completed onboarding
    return false;
  } catch (error) {
    console.error("Error checking onboarding status:", error);
    // In case of error, assume onboarding is not completed to be safe
    return false;
  }
};

/**
 * Check if user should be redirected to onboarding
 * @param {Object} user - The current user object from Firebase Auth
 * @returns {Promise<boolean>} - True if user should be redirected to onboarding
 */
export const shouldRedirectToOnboarding = async (user) => {
  // If no user, don't redirect
  if (!user) {
    return false;
  }

  // If user is anonymous, don't redirect (allow quiz taking)
  if (user.isAnonymous) {
    return false;
  }

  // Check if user has completed onboarding
  const hasCompletedOnboarding = await checkOnboardingStatus(user);
  return !hasCompletedOnboarding;
};
